import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import {
  findEmptyCanvasSlot,
  placeNodeNearby,
  toSlotBox,
  type CanvasSlotBox,
  type PlaceableSlot,
} from '../../services/canvasPlacementService'
import { NODE_WIDTH, NODE_HEIGHT } from '../../services/canvasLayoutConstants'
import {
  getAnchorPoint,
  getNodeWireEndpoint,
  type AnchorName,
  type WireEndpointSlot,
} from './wireGeometry'

export function useBoardPlacement({
  boardScopedNodes,
  optimisticPositions,
  boardMetrics,
  breakingWireCounterRef,
  setBreakingWires,
}: {
  boardScopedNodes: BoardChecklistItem[]
  optimisticPositions: Record<string, { x: number; y: number }>
  boardMetrics: { offsetX: number; offsetY: number }
  breakingWireCounterRef: MutableRefObject<number>
  setBreakingWires: Dispatch<SetStateAction<Array<{ id: string; path: string }>>>
}) {
  // NOTE: opening the drawer no longer auto-locks the node. A node is only
  // locked while an editor is actively in Edit mode inside the drawer; that
  // lock is acquired/released by the drawer via the edit-lock props below.

  // Every add-to-canvas path (manual toolbar, drag-drop, gate add, AI/Line
  // spawn) funnels through `findEmptyCanvasSlot`. The overlap check runs
  // against every node on the CURRENT BOOK (same checklistType, within its
  // recurring window) — book-wide, not breadcrumb-scoped — so a new item
  // never lands on top of a sibling-parent node the user can't see.
  function collectPlacementBoxes(excludeId?: string): CanvasSlotBox[] {
    return boardScopedNodes
      .filter((node) => node.id !== excludeId)
      .map((node) => toSlotBox({
        position: optimisticPositions[node.id] ?? node.position,
        nodeKind: node.nodeKind,
      }))
  }

  // Same as `collectPlacementBoxes` but carries each node's id so the drop
  // path can key displacement nudges back to the underlying record.
  function collectPlaceableSlots(excludeId?: string): PlaceableSlot[] {
    return boardScopedNodes
      .filter((node) => node.id !== excludeId)
      .map((node) => ({
        id: node.id,
        ...toSlotBox({
          position: optimisticPositions[node.id] ?? node.position,
          nodeKind: node.nodeKind,
        }),
      }))
  }

  function getNextFreePosition() {
    return findEmptyCanvasSlot({
      existing: collectPlacementBoxes(),
      newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
    })
  }

  function getNextChecklistGridPosition(preferred?: { x: number; y: number } | null) {
    return findEmptyCanvasSlot({
      existing: collectPlacementBoxes(),
      newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
      preferred: preferred ?? null,
    })
  }

  // Drops flow through `placeNodeNearby`, which either keeps the release
  // point verbatim, gently nudges one blocking neighbor to make room, or
  // snaps to the nearest empty grid cell — in that order. Never the far
  // top-left, so the drop lands where the user aimed (or a hair away).
  function resolveDropPosition(itemId: string, position: { x: number; y: number }) {
    return placeNodeNearby({
      existing: collectPlaceableSlots(itemId),
      newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
      preferred: { x: Math.max(0, position.x), y: Math.max(0, position.y) },
      allowDisplacement: true,
    })
  }

  function getRenderedNode(itemToRender: BoardChecklistItem) {
    return {
      ...itemToRender,
      position: {
        x: itemToRender.position.x + boardMetrics.offsetX,
        y: itemToRender.position.y + boardMetrics.offsetY,
      },
    }
  }

  function getRenderedAnchorPoint(itemToRender: BoardChecklistItem, anchor: AnchorName) {
    return getAnchorPoint(getRenderedNode(itemToRender), anchor)
  }

  function getRenderedWireEndpoint(itemToRender: BoardChecklistItem, role: 'in' | 'out', slot?: WireEndpointSlot) {
    return getNodeWireEndpoint(getRenderedNode(itemToRender), role, slot)
  }

  function buildConnectorPath(source: BoardChecklistItem, target: BoardChecklistItem) {
    const from = getRenderedWireEndpoint(source, 'out')
    const to = getRenderedWireEndpoint(target, 'in')
    const midY = Math.max(from.y + 28, (from.y + to.y) / 2)
    return `M ${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`
  }

  function animateBreakingWire(source: BoardChecklistItem, target: BoardChecklistItem) {
    const id = `${source.id}-${target.id}-${breakingWireCounterRef.current}`
    breakingWireCounterRef.current += 1
    const path = buildConnectorPath(source, target)
    setBreakingWires((current) => [...current, { id, path }])
    window.setTimeout(() => {
      setBreakingWires((current) => current.filter((wire) => wire.id !== id))
    }, 720)
  }

  return {
    collectPlacementBoxes,
    collectPlaceableSlots,
    getNextFreePosition,
    getNextChecklistGridPosition,
    resolveDropPosition,
    getRenderedNode,
    getRenderedAnchorPoint,
    getRenderedWireEndpoint,
    buildConnectorPath,
    animateBreakingWire,
  }
}
